import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Trash2, Search, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import DatePicker from '../../components/common/DatePicker';
import { Textarea } from '../../components/common/Input';
import InputMontant from '../../components/common/InputMontant';
import { getDeviseCourante } from '../../utils/format';

function formatGNF(n) {
  return Number(n || 0).toLocaleString('fr-FR') + ' ' + getDeviseCourante();
}

// FormBonCommande — création OU édition.
//   - bon === null  : création d'un brouillon
//   - bon !== null  : édition (autorisée uniquement si statut === 'brouillon',
//                     sinon mode lecture seule)
//
// Le formulaire suit la même logique que FormApprovisionnement.js (recherche
// produit + ajout de lignes + qté/prix + totaux live). Particularités :
//   - Choix du fournisseur explicite (dropdown, vs. fournisseur déjà choisi
//     dans FormApprovisionnement)
//   - Champ date_livraison_prevue
//   - Statut affiché en lecture seule + bouton "Enregistrer et envoyer"
export default function FormBonCommande({ bon, onClose, onSuccess }) {
  const isEdition = !!bon;
  const isReadOnly = isEdition && bon.statut !== 'brouillon';

  const [fournisseurs, setFournisseurs] = useState([]);
  const [fournisseurId, setFournisseurId] = useState('');
  const [dateLivraison, setDateLivraison] = useState('');
  const [notes, setNotes] = useState('');
  const [lignes, setLignes] = useState([]);
  const [search, setSearch] = useState('');
  const [produits, setProduits] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [cartons, setCartons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chargementBon, setChargementBon] = useState(isEdition);

  // Charger les fournisseurs actifs + les produits pour la recherche
  useEffect(() => {
    api.get('/fournisseurs').then(r => {
      // Backend renvoie un tableau ou { fournisseurs: [...] }
      const arr = Array.isArray(r.data) ? r.data : (r.data.fournisseurs || []);
      setFournisseurs(arr.filter(f => f.statut === 'actif'));
    }).catch(() => {});
    api.get('/produits', { params: { limit: 200 } })
      .then(r => setProduits(r.data.produits || []))
      .catch(() => {});
    api.get('/cartons').then(r => setCartons(r.data || [])).catch(() => {});
  }, []);

  // Si on édite, charger le détail du bon (lignes incluses)
  useEffect(() => {
    if (!isEdition) return;
    api.get(`/bons-commande/${bon.id}`).then(({ data }) => {
      setFournisseurId(data.fournisseur_id);
      setDateLivraison(data.date_livraison_prevue || '');
      setNotes(data.notes || '');
      setLignes((data.lignes || []).map(l => ({
        variante_id: l.variante_id,
        carton_id: l.carton_id || null,
        produit_nom: l.produit_nom,
        modele: l.modele,
        couleur: l.couleur,
        taille: l.taille_nom || '',
        code_variante: l.code_variante,
        quantite: l.quantite,
        prix_achat_unitaire: l.prix_achat_unitaire,
      })));
    }).catch(() => {
      toast.error('Erreur chargement du bon');
    }).finally(() => setChargementBon(false));
  }, [isEdition, bon]);

  // Filtrage local de la recherche produit
  useEffect(() => {
    if (!search.trim()) { setSearchResults([]); return; }
    const q = search.toLowerCase();
    const results = [];
    cartons.forEach(c => {
      if (c.nom?.toLowerCase().includes(q) || c.code?.toLowerCase().includes(q)) results.push({ carton: c });
    });
    produits.forEach(p => {
      (p.variantes || []).filter(v => v.actif).forEach(v => {
        if (
          p.nom.toLowerCase().includes(q) ||
          v.modele?.toLowerCase().includes(q) ||
          v.couleur?.toLowerCase().includes(q) ||
          (q.length >= 4 && v.code_variante?.toLowerCase().includes(q))
        ) {
          results.push({ produit: p, variante: v });
        }
      });
    });
    setSearchResults(results.slice(0, 12));
  }, [search, produits, cartons]);

  const ajouterLigne = (produit, variante) => {
    if (lignes.find(l => l.variante_id === variante.id)) {
      toast('Déjà ajouté — modifiez la quantité', { icon: '⚠️' });
      return;
    }
    // Ajout en TÊTE de liste — le dernier article ajouté apparaît au-dessus
    // des précédents (UX : tu vois immédiatement ce que tu viens d'ajouter).
    setLignes(prev => [{
      variante_id: variante.id,
      code_variante: variante.code_variante,
      produit_nom: produit.nom,
      modele: variante.modele,
      taille: variante.taille_nom || '',
      couleur: variante.couleur,
      quantite: 1,
      // Pré-rempli avec le dernier prix d'achat connu (modifiable).
      prix_achat_unitaire: variante.dernier_prix_achat || 0,
    }, ...prev]);
    setSearch('');
    setSearchResults([]);
  };

  const ajouterCarton = (carton) => {
    if (lignes.find(l => l.carton_id === carton.id)) {
      toast('Conditionnement déjà ajouté', { icon: '⚠️' });
      return;
    }
    const contenu = (carton.composants || [])
      .map(c => `${c.quantite}× ${c.produit_nom}${c.modele ? ` (${c.modele})` : ''}`).join(' + ');
    setLignes(prev => [{
      carton_id: carton.id, produit_nom: `${carton.nom}`,
      modele: contenu, taille: '', couleur: '',
      quantite: 1, prix_achat_unitaire: 0,
    }, ...prev]);
    setSearch('');
    setSearchResults([]);
  };

  const mettreAJour = (i, field, value) =>
    setLignes(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  const supprimer = (i) =>
    setLignes(prev => prev.filter((_, idx) => idx !== i));

  const total = lignes.reduce(
    (s, l) => s + (parseInt(l.quantite) || 0) * (parseFloat(l.prix_achat_unitaire) || 0),
    0
  );

  const valider = useCallback(() => {
    if (!fournisseurId) { toast.error('Sélectionnez un fournisseur'); return false; }
    if (lignes.length === 0) { toast.error('Ajoutez au moins un article'); return false; }
    for (const l of lignes) {
      if (!l.quantite || l.quantite <= 0) { toast.error(`Quantité invalide pour ${l.produit_nom}`); return false; }
      if (!l.prix_achat_unitaire || l.prix_achat_unitaire <= 0) { toast.error(`Prix manquant pour ${l.produit_nom}`); return false; }
    }
    return true;
  }, [fournisseurId, lignes]);

  // Enregistrer (création ou modification) → reste en brouillon
  const enregistrer = async () => {
    if (!valider()) return;
    setLoading(true);
    try {
      const payload = {
        fournisseur_id: fournisseurId,
        date_livraison_prevue: dateLivraison || null,
        notes: notes || null,
        lignes: lignes.map(l => (
          l.carton_id
            ? { carton_id: l.carton_id, quantite: parseInt(l.quantite), prix_achat_unitaire: parseFloat(l.prix_achat_unitaire) }
            : { variante_id: l.variante_id, quantite: parseInt(l.quantite), prix_achat_unitaire: parseFloat(l.prix_achat_unitaire) }
        )),
      };
      if (isEdition) {
        await api.put(`/bons-commande/${bon.id}`, payload);
        toast.success('Bon mis à jour');
      } else {
        await api.post('/bons-commande', payload);
        toast.success('Brouillon créé');
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur serveur');
    } finally {
      setLoading(false);
    }
  };

  // Enregistrer + passer immédiatement à statut « envoyé »
  const enregistrerEtEnvoyer = async () => {
    if (!valider()) return;
    setLoading(true);
    try {
      let bonId = bon?.id;
      const payload = {
        fournisseur_id: fournisseurId,
        date_livraison_prevue: dateLivraison || null,
        notes: notes || null,
        lignes: lignes.map(l => (
          l.carton_id
            ? { carton_id: l.carton_id, quantite: parseInt(l.quantite), prix_achat_unitaire: parseFloat(l.prix_achat_unitaire) }
            : { variante_id: l.variante_id, quantite: parseInt(l.quantite), prix_achat_unitaire: parseFloat(l.prix_achat_unitaire) }
        )),
      };
      if (isEdition) {
        await api.put(`/bons-commande/${bon.id}`, payload);
      } else {
        const { data } = await api.post('/bons-commande', payload);
        bonId = data.id;
      }
      await api.patch(`/bons-commande/${bonId}/statut`, { statut: 'envoye' });
      toast.success('Bon enregistré et marqué envoyé');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-3xl my-8 max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700 flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white">
              {isEdition ? (isReadOnly ? `Commande ${bon.numero} (${bon.statut})` : `Modifier ${bon.numero}`) : 'Nouvelle commande'}
            </h2>
            {!isReadOnly && (
              <p className="text-gray-400 text-xs mt-0.5">
                Le brouillon ne touche pas le stock ni la caisse — c'est juste la liste à envoyer au fournisseur.
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5 overflow-y-auto flex-1">
          {chargementBon ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Fournisseur + date livraison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Fournisseur *</label>
                  <select
                    value={fournisseurId}
                    onChange={e => setFournisseurId(e.target.value)}
                    disabled={isReadOnly}
                    className="w-full bg-gray-800 border border-gray-600 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500 disabled:opacity-60"
                  >
                    <option value="">— Sélectionner —</option>
                    {fournisseurs.map(f => (
                      <option key={f.id} value={f.id}>
                        {f.nom}{f.specialite ? ` (${f.specialite})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <DatePicker
                  label="Livraison prévue (optionnel)"
                  value={dateLivraison}
                  onChange={e => setDateLivraison(e.target.value)}
                  disabled={isReadOnly}
                />
              </div>

              {/* Recherche produit */}
              {!isReadOnly && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Ajouter un article</label>
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Nom, modèle, couleur, code..."
                      className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-yellow-500"
                    />
                    {searchResults.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl z-20 max-h-64 overflow-y-auto">
                        {searchResults.map((r) => r.carton ? (
                          <button
                            key={`c-${r.carton.id}`}
                            onClick={() => ajouterCarton(r.carton)}
                            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-700 transition-colors text-left border-l-2 border-yellow-500/60"
                          >
                            <div className="min-w-0">
                              <p className="text-white text-sm font-medium">{r.carton.nom} <span className="text-yellow-400/80 text-[11px]">conditionnement</span></p>
                              <p className="text-gray-400 text-xs truncate">{(r.carton.composants || []).map(c => `${c.quantite}× ${c.produit_nom}`).join(' + ')}</p>
                            </div>
                          </button>
                        ) : (
                          <button
                            key={`v-${r.variante.id}`}
                            onClick={() => ajouterLigne(r.produit, r.variante)}
                            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-700 transition-colors text-left"
                          >
                            <div>
                              <p className="text-white text-sm font-medium">{r.produit.nom}</p>
                              <p className="text-gray-400 text-xs">
                                {r.variante.modele} {r.variante.couleur} · {r.variante.code_variante}
                              </p>
                            </div>
                            <span className="text-yellow-400 text-xs ml-3 flex-shrink-0">
                              Stock actuel : {r.variante.stock_disponible || 0}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Lignes du bon */}
              {lignes.length > 0 ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2 px-2 text-xs text-gray-500 uppercase">
                    <div className="col-span-5">Produit</div>
                    <div className="col-span-2 text-center">Qté</div>
                    <div className="col-span-3 text-center">P.U. négocié</div>
                    <div className="col-span-1 text-right">Total</div>
                    <div className="col-span-1"></div>
                  </div>
                  {lignes.map((l, i) => (
                    <div key={l.carton_id || l.variante_id} className="grid grid-cols-12 gap-2 items-center bg-gray-800 rounded-xl px-3 py-2.5">
                      <div className="col-span-5 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{l.produit_nom}</p>
                        <p className="text-gray-500 text-xs truncate">
                          {l.carton_id ? l.modele : `${l.modele || ''} ${l.couleur || ''}`.trim()}
                        </p>
                        {l.carton_id && <p className="text-yellow-400/70 text-[10px]">Qté = conditionnements · Prix = prix d'un conditionnement</p>}
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number" min="1"
                          value={l.quantite}
                          onChange={e => mettreAJour(i, 'quantite', e.target.value)}
                          disabled={isReadOnly}
                          className="w-full bg-gray-900 border border-gray-700 text-white rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:border-yellow-500 disabled:opacity-60"
                        />
                      </div>
                      <div className="col-span-3">
                        <InputMontant
                          value={l.prix_achat_unitaire}
                          onChange={v => mettreAJour(i, 'prix_achat_unitaire', v)}
                          disabled={isReadOnly}
                          placeholder="Prix"
                          className="w-full bg-gray-900 border border-gray-700 text-white rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:border-yellow-500 disabled:opacity-60"
                        />
                      </div>
                      <div className="col-span-1 text-right text-yellow-400 text-sm font-bold whitespace-nowrap">
                        {formatGNF((parseInt(l.quantite) || 0) * (parseFloat(l.prix_achat_unitaire) || 0))}
                      </div>
                      <div className="col-span-1 text-right">
                        {!isReadOnly && (
                          <button onClick={() => supprimer(i)}
                            className="text-gray-500 hover:text-red-400 transition-colors">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                !isReadOnly && (
                  <div className="text-center py-8 text-gray-600 border border-dashed border-gray-700 rounded-xl">
                    <Plus size={28} className="mx-auto mb-2 opacity-40" />
                    <p className="text-sm">Aucun article — utilise la recherche ci-dessus</p>
                  </div>
                )
              )}

              {/* Notes */}
              <Textarea
                label="Notes / conditions (optionnel)"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Délai de livraison, conditions de paiement, etc."
                rows={2}
                disabled={isReadOnly}
              />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-700 p-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-400 text-sm">Total estimé</span>
            <span className="text-yellow-400 text-xl font-black">{formatGNF(total)}</span>
          </div>
          {!isReadOnly && (
            <div className="flex gap-2">
              <button onClick={onClose}
                className="flex-1 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl font-medium transition-colors">
                Annuler
              </button>
              <button onClick={enregistrer} disabled={loading}
                className="flex-1 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-bold disabled:opacity-50 transition-colors">
                {loading ? '…' : (isEdition ? 'Enregistrer' : 'Enregistrer brouillon')}
              </button>
              <button onClick={enregistrerEtEnvoyer} disabled={loading}
                className="flex-1 py-2.5 bg-yellow-500 hover:bg-yellow-400 text-black rounded-xl font-bold disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                {loading ? '…' : <><Send size={14} /> Enregistrer + envoyer</>}
              </button>
            </div>
          )}
          {isReadOnly && (
            <button onClick={onClose}
              className="w-full py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl font-medium transition-colors">
              Fermer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
